import React, { useEffect, useState } from 'react';

import { extractErrorMessage } from '../../../api/errors';
import { organisationsApi } from '../../../api/organisations';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import Input from '../../../components/ui/Input';
import { useFPO } from './FPOContext';

const NEW_FARMER = { full_name: '', phone: '', village: '', notes: '' };
const NEW_FARM = { label: '', land_acres: '', village: '', soil_type: '', primary_crop: '' };

export default function Farmers() {
  const { refresh } = useFPO();
  const [farmers, setFarmers] = useState([]);
  const [crops, setCrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(NEW_FARMER);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const loadAll = async () => {
    try {
      const [f, c] = await Promise.all([
        organisationsApi.listFarmers(),
        organisationsApi.listCrops(),
      ]);
      setFarmers(f);
      setCrops(c);
      setError('');
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setAddSubmitting(true);
    setAddError('');
    try {
      await organisationsApi.createFarmer(addForm);
      setAddForm(NEW_FARMER);
      setShowAdd(false);
      await loadAll();
      await refresh();
    } catch (err) {
      setAddError(extractErrorMessage(err));
    } finally {
      setAddSubmitting(false);
    }
  };

  const handleDelete = async (farmer) => {
    if (!window.confirm(`Delete ${farmer.full_name}? Their farms will also be removed.`)) return;
    try {
      await organisationsApi.deleteFarmer(farmer.id);
      await loadAll();
      await refresh();
    } catch (err) {
      window.alert(extractErrorMessage(err));
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-500">Loading farmers…</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Member farmers</h1>
          <p className="mt-1 text-sm text-gray-600">
            People whose land and produce roll up into this FPO. Add their farms once they're listed here.
          </p>
        </div>
        <Button onClick={() => setShowAdd((v) => !v)}>
          {showAdd ? 'Cancel' : '+ Add farmer'}
        </Button>
      </div>

      {showAdd && (
        <Card className="p-6">
          <h2 className="text-base font-semibold text-gray-900">Add a farmer</h2>
          <form className="mt-4 grid gap-4 sm:grid-cols-2" onSubmit={handleAddSubmit}>
            <Input
              name="full_name"
              label="Full name"
              value={addForm.full_name}
              onChange={(e) => setAddForm({ ...addForm, full_name: e.target.value })}
              required
              placeholder="Ramesh Patil"
            />
            <Input
              name="phone"
              label="Phone"
              type="tel"
              value={addForm.phone}
              onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })}
              placeholder="9876543210"
            />
            <Input
              name="village"
              label="Village"
              value={addForm.village}
              onChange={(e) => setAddForm({ ...addForm, village: e.target.value })}
              placeholder="Sinnar"
            />
            <Input
              name="notes"
              label="Notes (optional)"
              value={addForm.notes}
              onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}
              placeholder="2 acres of grapes, 1 of tomato"
            />
            {addError && (
              <p className="text-sm text-red-600 sm:col-span-2">{addError}</p>
            )}
            <div className="sm:col-span-2">
              <Button type="submit" disabled={addSubmitting}>
                {addSubmitting ? 'Saving…' : 'Save farmer'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      {farmers.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-sm text-gray-500">
            No farmers yet. Add your first member with the button above.
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
         <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Farmer', 'Village', 'Phone', 'Farms', ''].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {farmers.map((farmer) => (
                <React.Fragment key={farmer.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {farmer.full_name}
                      {farmer.notes && (
                        <p className="mt-0.5 text-xs font-normal text-gray-500">{farmer.notes}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{farmer.village || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{farmer.phone || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{farmer.farm_count}</td>
                    <td className="px-4 py-3 text-right text-sm">
                      <button
                        type="button"
                        onClick={() => setExpandedId(expandedId === farmer.id ? null : farmer.id)}
                        className="text-green-700 hover:text-green-800"
                      >
                        {expandedId === farmer.id ? 'Hide farms' : 'Show farms'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(farmer)}
                        className="ml-4 text-red-600 hover:text-red-700"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                  {expandedId === farmer.id && (
                    <tr className="bg-green-50/40">
                      <td colSpan={5} className="px-4 py-4">
                        <FarmsPanel farmer={farmer} crops={crops} onChanged={loadAll} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
         </div>
        </Card>
      )}
    </div>
  );
}

function FarmsPanel({ farmer, crops, onChanged }) {
  const [farms, setFarms] = useState(null);
  const [error, setError] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(NEW_FARM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const loadFarms = async () => {
    try {
      const all = await organisationsApi.listFarms();
      setFarms(all.filter((f) => f.farmer === farmer.id));
      setError('');
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  useEffect(() => {
    loadFarms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');
    try {
      await organisationsApi.createFarm({
        ...form,
        farmer: farmer.id,
        primary_crop: form.primary_crop || null,
        land_acres: form.land_acres,
      });
      setForm(NEW_FARM);
      setShowAdd(false);
      await loadFarms();
      await onChanged();
    } catch (err) {
      setFormError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (farm) => {
    if (!window.confirm(`Delete ${farm.label || 'this farm'}?`)) return;
    try {
      await organisationsApi.deleteFarm(farm.id);
      await loadFarms();
      await onChanged();
    } catch (err) {
      window.alert(extractErrorMessage(err));
    }
  };

  if (farms === null) {
    return <p className="text-xs text-gray-500">Loading farms…</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-green-800">
          Farms ({farms.length})
        </p>
        <button
          type="button"
          onClick={() => setShowAdd((v) => !v)}
          className="text-xs font-semibold text-green-700 hover:text-green-800"
        >
          {showAdd ? 'Cancel' : '+ Add farm'}
        </button>
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      {farms.length === 0 ? (
        <p className="text-xs text-gray-500">No farms yet.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {farms.map((farm) => (
            <li
              key={farm.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-green-100 bg-white px-3 py-2 text-sm"
            >
              <div>
                <span className="font-medium text-gray-900">{farm.label || 'Unnamed plot'}</span>
                <span className="ml-2 text-gray-600">· {Number(farm.land_acres).toFixed(2)} acres</span>
                {farm.primary_crop_name && (
                  <span className="ml-2 text-gray-600">· {farm.primary_crop_name}</span>
                )}
                {farm.village && <span className="ml-2 text-gray-500">({farm.village})</span>}
              </div>
              <button
                type="button"
                onClick={() => handleDelete(farm)}
                className="text-xs text-red-600 hover:text-red-700"
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}

      {showAdd && (
        <form className="mt-2 grid gap-3 sm:grid-cols-2" onSubmit={handleSubmit}>
          <Input
            label="Label"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="Main plot"
          />
          <Input
            label="Acres"
            type="number"
            step="0.01"
            min="0"
            value={form.land_acres}
            onChange={(e) => setForm({ ...form, land_acres: e.target.value })}
            required
            placeholder="2.5"
          />
          <Input
            label="Village"
            value={form.village}
            onChange={(e) => setForm({ ...form, village: e.target.value })}
          />
          <Input
            label="Soil type"
            value={form.soil_type}
            onChange={(e) => setForm({ ...form, soil_type: e.target.value })}
            placeholder="Black soil"
          />
          <div className="flex flex-col gap-1 sm:col-span-2">
            <label className="text-sm font-medium text-gray-700">Primary crop (optional)</label>
            <select
              value={form.primary_crop}
              onChange={(e) => setForm({ ...form, primary_crop: e.target.value })}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
            >
              <option value="">— none —</option>
              {crops.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.category})
                </option>
              ))}
            </select>
          </div>
          {formError && <p className="text-sm text-red-600 sm:col-span-2">{formError}</p>}
          <div className="sm:col-span-2">
            <Button type="submit" size="sm" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save farm'}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
